/**
 * Masterclass registration endpoint config.
 *
 * The destination is read from `VITE_MASTERCLASS_URL` so the form can point at
 * whatever collects registrations (webhook, CRM endpoint, form service)
 * without a code change. If it is not set, the form fails loudly with a clear
 * message instead of silently dropping leads.
 */
import { z } from "zod";

export const MASTERCLASS_URL: string =
  (import.meta.env.VITE_MASTERCLASS_URL as string | undefined)?.trim() || "";

/**
 * Resolve the endpoint at call time. In dev/preview builds an e2e harness may
 * set `window.__MASTERCLASS_URL__` so Playwright can exercise the full
 * success/error flow without a real CRM endpoint. Never honored in production.
 */
export function resolveMasterclassUrl(): string {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = (window as unknown as { __MASTERCLASS_URL__?: string }).__MASTERCLASS_URL__;
    if (typeof override === "string" && override.trim()) return override.trim();
  }
  return MASTERCLASS_URL;
}

export const isMasterclassUrlConfigured = () => resolveMasterclassUrl().length > 0;


export const registrationSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty({ message: "Please enter your name." })
    .max(100, { message: "Name must be under 100 characters." }),
  email: z
    .string()
    .trim()
    .email({ message: "Enter a valid email address." })
    .max(255, { message: "Email must be under 255 characters." }),
  phone: z
    .string()
    .trim()
    .max(30, { message: "Phone must be under 30 characters." })
    .optional()
    .or(z.literal("")),
  goal: z
    .string()
    .trim()
    .max(500, { message: "Keep this under 500 characters." })
    .optional()
    .or(z.literal("")),
});

export type RegistrationInput = z.infer<typeof registrationSchema>;

/**
 * Confirmation record echoed back by the registration endpoint. Everything is
 * optional: some endpoints answer with an empty body. The thank-you page only
 * renders the fields the server actually returned — it never invents values
 * or falls back to what the visitor typed.
 */
export const confirmationSchema = z
  .object({
    confirmation_id: z.string().trim().min(1).max(120).optional(),
    name: z.string().trim().min(1).max(200).optional(),
    email: z.string().trim().email().max(255).optional(),
    session_date: z.string().trim().min(1).max(120).optional(),
    session_time: z.string().trim().min(1).max(120).optional(),
    join_url: z.string().trim().url().max(2048).optional(),
  })
  .passthrough();

export type MasterclassConfirmation = {
  confirmationId?: string;
  name?: string;
  email?: string;
  sessionDate?: string;
  sessionTime?: string;
  joinUrl?: string;
};

/** Registration confirmation as handed to the thank-you screen. */
export type MasterclassConfirmationState = {
  registration: MasterclassConfirmation;
  /** Epoch ms the confirmation was issued — used to reject stale history state. */
  issuedAt: number;
  correlationId?: string;
};

/** Confirmations older than this are treated as stale and are not displayed. */
export const CONFIRMATION_MAX_AGE_MS = 30 * 60 * 1000;

function parseConfirmation(raw: unknown): MasterclassConfirmation | null {
  const parsed = confirmationSchema.safeParse(raw);
  if (!parsed.success) return null;
  const d = parsed.data;
  const out: MasterclassConfirmation = {
    confirmationId: d.confirmation_id,
    name: d.name,
    email: d.email,
    sessionDate: d.session_date,
    sessionTime: d.session_time,
    joinUrl: d.join_url,
  };
  return Object.values(out).some(Boolean) ? out : null;
}

export async function submitMasterclassRegistration(
  payload: RegistrationInput & Record<string, unknown>,
  signal?: AbortSignal,
): Promise<MasterclassConfirmation | null> {
  if (!isMasterclassUrlConfigured()) {
    throw new Error(
      "Registration isn't connected yet. Please email support and we'll save your seat.",
    );
  }

  const res = await fetch(resolveMasterclassUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    throw new Error(
      res.status >= 500
        ? "Our registration service hiccupped. Please try again in a moment."
        : "We couldn't save that registration. Check your details and try again.",
    );
  }

  // A missing or malformed body is fine — the registration still succeeded, we
  // just have nothing server-confirmed to display.
  try {
    return parseConfirmation(await res.json());
  } catch {
    return null;
  }
}
